// =============================================================================
// ESPOLIDAR WEB ADMIN - WORKSPACE HOOKS
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { workspaceApi, zonesApi } from '../services/api'
import { wsService } from '../services/websocket'
import type { WorkspaceConfigCreate, ZoneCartesianCreate } from '../types'
import toast from 'react-hot-toast'

// =============================================================================
// WORKSPACE HOOKS
// =============================================================================

export function useWorkspaceConfig(deviceId: string) {
  return useQuery({
    queryKey: ['workspace', deviceId],
    queryFn: () => workspaceApi.get(deviceId),
    enabled: !!deviceId,
  })
}

export function useUpdateWorkspace() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ deviceId, data }: { deviceId: string; data: Partial<WorkspaceConfigCreate> }) =>
      workspaceApi.update(deviceId, data),
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: ['workspace', deviceId] })
      // Notifica il player per sincronizzare la configurazione
      wsService.notifyConfigUpdate(deviceId)
      toast.success('Area di lavoro aggiornata e sincronizzata')
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { detail?: string }, status?: number } }
      const detail = axiosError.response?.data?.detail || 'Errore sconosciuto'
      const status = axiosError.response?.status
      console.error('Workspace update error:', { status, detail, error })
      toast.error(`Errore aggiornamento area di lavoro: ${detail}`)
    },
  })
}

// =============================================================================
// CARTESIAN ZONE HOOKS
// =============================================================================

export function useCreateCartesianZone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ deviceId, data }: { deviceId: string; data: ZoneCartesianCreate }) =>
      zonesApi.createCartesian(deviceId, data),
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: ['zones', deviceId] })
      // Notifica il player per sincronizzare la configurazione
      wsService.notifyConfigUpdate(deviceId)
      toast.success('Zona creata e sincronizzata')
    },
    onError: () => {
      toast.error('Errore creazione zona')
    },
  })
}
